import { useEffect, useMemo, useState } from "react";
import { ModalContainer } from "../../components/ModalContainer";
import { Typography, useTheme, Grid2, Box, Link, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { EGBasicSelect } from "../../../../../MainComponents/Select/EGBasicSelect";
import _ from "lodash";
import { EGInput } from "../../../../../MainComponents/Input/EGInput";
import { InvestigationButton } from "../../components/InvestigationButton";
import { enqueueSnackbar } from "notistack";
import { EGSwitch } from "../../../../../MainComponents/Switch/Switch";
import EGDatePicker from "../../../../../MainComponents/DatePicker/EGDatePicker";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { ChipParticipant } from "../../components/InvestigationComponents";
import { HelpIcon } from "../../../../../MainComponents/HelpIcon/HelpIcon";
import { todoSchema } from "../schemas";
import { useConstants } from "../../Main/useConstants";
import { SectionedSelect } from "../../../../../MainComponents/Select/SectionedSelect";
import { useReport } from "../../../../../../context/report/ReportContext";

const validateForm = (form) => {
  const { name, dedicatedHours } = form;
  const errors = {};
  if (name.trim() === "") {
    errors.name = true;
  }
  if (dedicatedHours.trim() !== "" && !/^\d+$/.test(dedicatedHours)) {
    errors.dedicatedHours = true;
  }
  return [Object.entries(errors).length === 0 ? true : false, errors];
};

export const AddTodoForActivity = ({
  show,
  onHide,
  todos,
  setTodos,
  selectedTodo,
  setSelectedTodo,
}) => {
  const { state } = useReport();
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const {
    enums,
    functions: { getUserLabel },
    plans: { statusTodosOpts },
    selects: { hoursOpts, typeUserLabels },
  } = useConstants();
  const jaguarState = useSelector((state) => state.jaguar);
  const { email, username } = useSelector((state) => state.auth);

  const [todo, setTodo] = useState(_.cloneDeep(todoSchema));

  const [party, setParty] = useState([]);
  const [participantToAdd, setParticipantToAdd] = useState("");

  const [errors, setErrors] = useState({});
  const [triedToSave, setTriedToSave] = useState(false);

  const [activeDetails, setActiveDetails] = useState(false);
  const [addDedicatedHours, setAddDedicatedHours] = useState(false);

  // Select for assign the activity
  const assignedToSections = useMemo(() => {
    const { ME, RESOURCES, EXTERNAL, SYSTEM } = enums.typeUser;
    const usersWanted = [ME, RESOURCES, EXTERNAL, SYSTEM];

    let newSections = [];

    usersWanted.forEach((value) => {
      if (value === ME) {
        newSections.push({
          key: value,
          options: [
            {
              value: email,
              label: username || email || t("activityPlan.assign_to_me"),
            },
          ],
        });
        return;
      }
      const path = _.get(enums, ["typeUserRoutes", value]);
      const node = _.get(state, path);
      const fromArray =
        (node &&
          node.formatted &&
          Array.isArray(node.formatted) &&
          node.formatted) ||
        (node && node.raw && Array.isArray(node.raw) && node.raw) ||
        (Array.isArray(node) && node) ||
        [];

      newSections.push({
        label: typeUserLabels[value],
        key: value,
        options: fromArray,
      });
    });

    // Disable party from selections
    party.forEach((participant) => {
      const { id, from } = participant ?? {};
      const findedSectionIdx = newSections.findIndex(
        (section) => section.key === from
      );
      if (findedSectionIdx !== -1) {
        newSections[findedSectionIdx].options = newSections[
          findedSectionIdx
        ].options.map((option) => {
          if (option.value === id) {
            return { ...option, disabled: true };
          }
          return option;
        });
      }
    });

    return newSections;
  }, [party, state, enums, email, username]);

  // Select for add participants
  const participantsSections = useMemo(() => {
    const { ME, RESOURCES, EXTERNAL, SYSTEM } = enums.typeUser;
    const usersWanted = [ME, RESOURCES, EXTERNAL, SYSTEM];

    let newSections = [];

    usersWanted.forEach((value) => {
      if (value === ME) {
        newSections.push({
          key: value,
          options: [
            {
              value: email,
              label: username || email || t("activityPlan.participate_in_the_activity"),
            },
          ],
        });
        return;
      }
      const path = _.get(enums, ["typeUserRoutes", value]);
      const node = _.get(state, path);
      const fromArray =
        (node &&
          node.formatted &&
          Array.isArray(node.formatted) &&
          node.formatted) ||
        (node && node.raw && Array.isArray(node.raw) && node.raw) ||
        (Array.isArray(node) && node) ||
        [];
      newSections.push({
        label: typeUserLabels[value],
        key: value,
        options: fromArray,
      });
    });

    // Kick assigned from selections
    const { id, from } = todo?.assignedTo ?? {};
    const findedSectionIdx = newSections.findIndex(
      (section) => section.key === from
    );
    if (findedSectionIdx !== -1) {
      newSections[findedSectionIdx].options = newSections[
        findedSectionIdx
      ].options.map((option) => {
        if (option.value === id) {
          return { ...option, disabled: true };
        }
        return option;
      });
    }

    // Disable party from selections
    party.forEach((participant) => {
      const { id, from } = participant ?? {};
      const findedSectionIdx = newSections.findIndex(
        (section) => section.key === from
      );
      if (findedSectionIdx !== -1) {
        newSections[findedSectionIdx].options = newSections[
          findedSectionIdx
        ].options.map((option) => {
          if (option.value === id) {
            return { ...option, disabled: true };
          }
          return option;
        });
      }
    });

    return newSections;
  }, [todo.assignedTo, party, jaguarState, email, username]);

  useEffect(() => {
    if (selectedTodo != null) {
      if (typeof todos[selectedTodo] === "string") {
        setTodo((prev) => ({
          ...prev,
          name: todos[selectedTodo],
        }));
      } else {
        setTodo(todos[selectedTodo]);
        setParty(todos[selectedTodo].party || []);
        setActiveDetails(todos[selectedTodo].details ? true : false);
        setAddDedicatedHours(
          todos[selectedTodo].dedicatedHours != "" ? true : false
        );
      }
    }
  }, [selectedTodo]);

  useEffect(() => {
    if (triedToSave) {
      const [_, errors] = validateForm(todo);
      setErrors(errors);
    }
  }, [todo]);

  useEffect(() => {
    if (!show) {
      resetValues();
    }
  }, [show]);

  const handleChange = (e) => {
    setTodo({ ...todo, [e.target.name]: e.target.value });
  };

  const handleChangeResponsible = ({ value, section, label }) => {
    setParticipantToAdd(null);
    setTodo({ ...todo, assignedTo: { id: value, from: section, label } });
  };

  const handleAddParticipant = () => {
    setParty([...party, participantToAdd]);
    setParticipantToAdd(null);
  };

  const handleDeleteParticipant = (idx) => {
    setParty(party.filter((_, i) => i !== idx));
  };

  const handleClose = () => {
    resetValues();
    onHide();
  };

  const handleSubmit = () => {
    const [isValid, errors] = validateForm(todo);
    setErrors(errors);
    setTriedToSave(true);
    if (!isValid) {
      enqueueSnackbar({
        variant: "error",
        message: t("resources.missing_required_inputs"),
      });
      return;
    }
    if (selectedTodo == null) {
      setTodos([
        ...todos,
        {
          ...todo,
          party,
        },
      ]);
    } else {
      const newtodos = _.cloneDeep(todos);
      newtodos[selectedTodo] = {
        ...todo,
        party,
      };
      setTodos(newtodos);
    }
    handleClose();
  };

  const resetValues = () => {
    setTodo(_.cloneDeep(todoSchema));
    setSelectedTodo(null);
    setParty([]);
    setParticipantToAdd("");
    setErrors({});
    setTriedToSave(false);
    setActiveDetails(false);
    setAddDedicatedHours(false);
  };

  const returnIsValid = (error) => {
    if (!triedToSave) return null;
    if (error) return false;
    return true;
  };

  return (
    <ModalContainer show={show} onHide={onHide}>
      <Typography
        fontWeight={700}
        fontSize={28}
        color={theme.palette.ethicsBlue.main}
      >
        {t("activityPlan.add_task")}
      </Typography>
      <Typography fontSize={16} color="oxfordBlue">
        {t("activityPlan.add_task_description")}
      </Typography>

      <div className="hr-div"></div>

      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <SectionedSelect
            label={t("activityPlan.responsible")}
            name="assignedTo"
            value={todo.assignedTo?.id ?? ""}
            onChange={handleChangeResponsible}
            sections={assignedToSections}
            fallbackLabel={getUserLabel(todo.assignedTo)}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <EGInput
            label={t("activityPlan.task")}
            name="name"
            value={todo.name}
            onChange={handleChange}
            required={true}
            isValid={returnIsValid(errors?.name) ?? null}
            showSuccessValidation
            showHelpText={false}
          />
          {!activeDetails && (
            <Box display="flex" justifyContent="flex-end">
              <Link
                onClick={() => setActiveDetails(true)}
                underline="hover"
                mt={0.5}
              >
                + {t("activityPlan.add_details")}
              </Link>
            </Box>
          )}
        </Grid2>

        {activeDetails && (
          <Grid2
            size={12}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
          >
            <EGInput
              label={t("activityPlan.details")}
              name="details"
              value={todo.details}
              onChange={handleChange}
              type="textarea"
              multiline
              rows={3}
            />
          </Grid2>
        )}

        <Grid2 size={{ xs: 12, md: 6 }}>
          <EGBasicSelect
            label={t("activityPlan.status")}
            name="completed"
            value={todo.completed}
            onChange={handleChange}
            options={statusTodosOpts}
          />
        </Grid2>

        <Grid2
          size={{ xs: 12, md: 6 }}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <EGDatePicker
            label={`${t("activityPlan.date")}`}
            name="deadline"
            value={todo.deadline ? dayjs(todo.deadline) : null}
            onChange={(e) =>
              handleChange({
                target: { name: "deadline", value: e.toISOString() },
              })
            }
            className="date-picker-deadline"
            format="DD/MMM/YYYY"
          />

          <Box width={140}>
            <EGBasicSelect
              label={t("activityPlan.time")}
              name="timeDeadline"
              value={todo.timeDeadline}
              onChange={handleChange}
              options={hoursOpts}
              showHelpText={false}
            />
          </Box>
        </Grid2>

        <Grid2
          size={{ xs: 12, lg: 6 }}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center">
            <Typography
              fontSize={16}
              fontWeight={600}
              color={theme.palette.secondary.main}
            >
              {t("activityPlan.add_dedicated_hours")}
            </Typography>
            <div style={{ display: "inline" }}>
              <HelpIcon
                text={t("activityPlan.help_text")}
                size="small"
                placement="top"
                sx={{ marginLeft: 1 }}
                type="question"
              />
            </div>
          </Box>
          <EGSwitch
            size="small"
            checked={addDedicatedHours}
            onChange={(e) => setAddDedicatedHours(e.target.checked)}
          />
        </Grid2>

        {addDedicatedHours && (
          <Grid2 size={{ xs: 12, lg: 6 }}>
            <EGInput
              label={t("activityPlan.dedicated_hours")}
              name="dedicatedHours"
              value={todo.dedicatedHours}
              placeholder="Colocar solo números"
              onChange={handleChange}
              isValid={returnIsValid(errors?.dedicatedHours) ?? null}
              showHelpText={false}
            />
          </Grid2>
        )}

        <Grid2 size={{ xs: 12, md: 6 }} container spacing={2}>
          <Grid2 size={9}>
            <SectionedSelect
              label={t("activityPlan.participants")}
              name="assignedTo"
              value={participantToAdd?.id ?? ""}
              onChange={({ value, section, label }) =>
                setParticipantToAdd({ id: value, from: section, label })
              }
              sections={participantsSections}
            />
          </Grid2>
          <Grid2 size={3}>
            <InvestigationButton
              label={`${t("activityPlan.add_alone")}`}
              icon={false}
              size="medium"
              onClick={handleAddParticipant}
              sx={{ width: "100%" }}
              disabled={!participantToAdd}
            />
          </Grid2>
        </Grid2>

        <Grid2 size={12} container spacing={2}>
          {party.map((participant, idx) => (
            <ChipParticipant
              key={idx}
              idx={idx}
              role={
                typeof participant === "string"
                  ? t("activityPlan.participant")
                  : typeUserLabels[participant.from]
              }
              name={
                typeof participant === "string"
                  ? participant
                  : getUserLabel(participant) || t("activityPlan.user_not_found")
              }
              handleDeleteParticipant={() => handleDeleteParticipant(idx)}
            />
          ))}
        </Grid2>
      </Grid2>

      {/* Buttons */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="flex-end"
        mt={2}
      >
        <InvestigationButton
          label={t("activityPlan.cancel")}
          icon="false"
          variant="main"
          outlined
          size="medium"
          onClick={onHide}
        />
        <InvestigationButton
          label={t("activityPlan.save")}
          icon="false"
          variant="main"
          size="medium"
          onClick={handleSubmit}
        />
      </Stack>
    </ModalContainer>
  );
};
